import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Heart, LoaderCircle, RefreshCw, Sparkles, WandSparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CreateGenerationInput } from "../../shared/contracts";
import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { useCapabilities } from "../capabilities";
import { PageHeader } from "../components/PageHeader";

const DRAFT_KEY = "gameframe:generation-draft";
const QUICK_WORDS = ["电影感", "超精细", "体积光", "史诗构图"];
const STYLES: Array<{ value: CreateGenerationInput["style"]; label: string; caption: string }> = [
  { value: "cyberpunk", label: "赛博朋克", caption: "霓虹都市" },
  { value: "dark_fantasy", label: "黑暗奇幻", caption: "史诗秘境" },
  { value: "anime", label: "日式动漫", caption: "鲜明叙事" },
  { value: "space_opera", label: "太空歌剧", caption: "深空奇观" },
  { value: "wuxia", label: "国风武侠", caption: "东方意境" },
];
const RATIOS: Array<{ value: CreateGenerationInput["aspectRatio"]; label: string; use: string }> = [
  { value: "16:9", label: "16:9", use: "桌面" },
  { value: "9:16", label: "9:16", use: "手机" },
  { value: "1:1", label: "1:1", use: "方形" },
];
const TERMINAL = new Set(["SUCCEEDED", "FAILED", "REJECTED", "TIMED_OUT"]);

function readDraft(): CreateGenerationInput {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) return { prompt: "", style: "cyberpunk", aspectRatio: "16:9", ...JSON.parse(raw) };
  } catch {
    localStorage.removeItem(DRAFT_KEY);
  }
  return { prompt: "", style: "cyberpunk", aspectRatio: "16:9" };
}

export function GeneratePage() {
  const initialDraft = useMemo(readDraft, []);
  const [prompt, setPrompt] = useState(initialDraft.prompt);
  const [style, setStyle] = useState(initialDraft.style);
  const [aspectRatio, setAspectRatio] = useState(initialDraft.aspectRatio);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [quotaMessage, setQuotaMessage] = useState<string | null>(null);
  const { me, requestLogin } = useAuth();
  const { fubeiEnabled } = useCapabilities();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem(DRAFT_KEY);
  }, []);

  const createTask = useMutation({
    mutationFn: () => api.createGeneration({ prompt, style, aspectRatio }),
    onSuccess: (task) => {
      setTaskId(task.id);
      void api.track("generation_started", { style, aspectRatio });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const taskQuery = useQuery({
    queryKey: ["generation", taskId],
    queryFn: () => api.generation(taskId!),
    enabled: Boolean(taskId),
    initialData: taskId && createTask.data?.id === taskId ? createTask.data : undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL.has(status) ? false : 1_000;
    },
  });
  const task = taskQuery.data;
  const artworkQuery = useQuery({
    queryKey: ["artwork", task?.artworkId],
    queryFn: () => api.artwork(task!.artworkId!),
    enabled: task?.status === "SUCCEEDED" && Boolean(task.artworkId),
  });

  useEffect(() => {
    if (task?.status === "SUCCEEDED") {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["artworks"] });
    }
  }, [task?.status, queryClient]);

  const favorite = useMutation({
    mutationFn: () => api.updateArtwork(artworkQuery.data!.id, { isFavorite: !artworkQuery.data!.isFavorite }),
    onSuccess: (artwork) => queryClient.setQueryData(["artwork", artwork.id], artwork),
  });
  const download = useMutation({
    mutationFn: () => api.downloadArtwork(artworkQuery.data!.id),
    onSuccess: (result) => {
      void api.track("artwork_downloaded", { variant: result.variant });
      window.location.assign(result.url);
    },
  });

  const draft = { prompt, style, aspectRatio };
  const persistDraft = () => localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  const submit = () => {
    setQuotaMessage(null);
    if (!me) {
      requestLogin("/generate", persistDraft);
      return;
    }
    if ((me.quota.available ?? 0) < 1) {
      if (fubeiEnabled) navigate("/pricing");
      else setQuotaMessage("本期生成次数已用完，支付功能暂未开放");
      return;
    }
    createTask.mutate();
  };
  const appendQuickWord = (word: string) => {
    const separator = prompt.trim() ? "，" : "";
    setPrompt((current) => (current + separator + word).slice(0, 500));
  };
  const resetResult = () => {
    setTaskId(null);
    createTask.reset();
  };
  const busy = createTask.isPending || (task && !TERMINAL.has(task.status));
  const error = createTask.error ?? taskQuery.error;

  return (
    <section>
      <PageHeader
        eyebrow="AI Wallpaper Studio"
        title="把脑海里的世界，变成壁纸。"
        subtitle="描述场景，选择风格与画幅。剩下的交给 GAMEFRAME。"
      />
      <div className="studio-layout">
        <form className="control-panel" onSubmit={(event) => { event.preventDefault(); submit(); }}>
          <label className="field-label" htmlFor="prompt"><span>画面描述</span><small>{prompt.length} / 500</small></label>
          <textarea
            id="prompt"
            className="prompt-input"
            value={prompt}
            maxLength={500}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="例如：孤独的装甲旅人站在雨后的天台，凝视远方宏伟的霓虹都市…"
            required
          />
          <div className="quick-words" aria-label="快捷提示词">
            {QUICK_WORDS.map((word) => <button key={word} type="button" onClick={() => appendQuickWord(word)}>+ {word}</button>)}
          </div>

          <fieldset className="field-group">
            <legend className="field-label"><span>游戏风格</span><small>选择 1 项</small></legend>
            <div className="style-grid">
              {STYLES.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  className={style === option.value ? "style-option selected style-" + index : "style-option style-" + index}
                  aria-pressed={style === option.value}
                  onClick={() => setStyle(option.value)}
                >
                  <strong>{option.label}</strong><small>{option.caption}</small>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="field-group">
            <legend className="field-label"><span>画面比例</span><small>输出规格</small></legend>
            <div className="ratio-grid">
              {RATIOS.map((ratio) => (
                <button key={ratio.value} type="button" className={aspectRatio === ratio.value ? "ratio-option selected" : "ratio-option"} aria-pressed={aspectRatio === ratio.value} onClick={() => setAspectRatio(ratio.value)}>
                  <strong>{ratio.label}</strong><small>{ratio.use}</small>
                </button>
              ))}
            </div>
          </fieldset>

          <button className="button button-primary button-full generate-button" type="submit" disabled={!prompt.trim() || Boolean(busy)}>
            {busy ? <><LoaderCircle className="spin" size={18} />生成中</> : <><WandSparkles size={18} />立即生成</>}
          </button>
          <p className="cost-copy">消耗 1 次 · 失败不扣次 · {me ? "剩余 " + me.quota.available + " 次" : "登录后开始创作"}</p>
          {(quotaMessage || error) && <p className="form-error" role="alert">{quotaMessage ?? (error instanceof ApiClientError ? error.message : "提交失败，请重试")}</p>}
        </form>

        <GenerationStage
          task={task}
          artwork={artworkQuery.data}
          onRetry={() => createTask.mutate()}
          onReset={resetResult}
          onFavorite={() => favorite.mutate()}
          onDownload={() => download.mutate()}
          downloadBusy={download.isPending}
        />
      </div>
    </section>
  );
}

function GenerationStage({ task, artwork, onRetry, onReset, onFavorite, onDownload, downloadBusy }: {
  task: Awaited<ReturnType<typeof api.generation>> | undefined;
  artwork: Awaited<ReturnType<typeof api.artwork>> | undefined;
  onRetry(): void;
  onReset(): void;
  onFavorite(): void;
  onDownload(): void;
  downloadBusy: boolean;
}) {
  if (!task) {
    return (
      <div className="result-stage stage-idle">
        <div className="stage-orbit"><Sparkles size={34} /><i /><i /></div>
        <div><p className="eyebrow">Ready to create</p><h2>你的下一张游戏壁纸<br />从一句描述开始。</h2><p>选择左侧参数后提交。生成完成的作品会自动进入作品库。</p></div>
      </div>
    );
  }
  if (task.status === "QUEUED" || task.status === "GENERATING") {
    return (
      <div className="result-stage stage-progress" aria-live="polite">
        <div className="progress-visual"><LoaderCircle className="spin" size={42} /><span>QWEN</span></div>
        <div><p className="eyebrow">Generation in progress</p><h2>{task.status === "QUEUED" ? "正在进入生成队列" : "正在渲染高清细节"}</h2><p>模型正在理解描述、构建场景并渲染细节。离开页面不会取消任务。</p></div>
        <ol className="generation-steps"><li className="done">理解描述</li><li className={task.status === "GENERATING" ? "active" : ""}>构建场景</li><li>渲染细节</li><li>完成落盘</li></ol>
      </div>
    );
  }
  if (task.status !== "SUCCEEDED") {
    return (
      <div className="result-stage stage-error">
        <div className="state-icon"><RefreshCw size={28} /></div>
        <p className="eyebrow">Generation interrupted</p>
        <h2>{task.status === "REJECTED" ? "内容未通过模型安全策略" : "生成任务暂时失败"}</h2>
        <p>{task.failureReason ?? "本次未消耗生成次数，提示词和参数已保留。"}</p>
        <div className="stage-actions"><button className="button button-secondary" onClick={onReset}>修改参数</button><button className="button button-primary" onClick={onRetry}><RefreshCw size={16} />重新生成</button></div>
      </div>
    );
  }
  return (
    <div className="result-stage stage-success" style={artwork ? { backgroundImage: "linear-gradient(0deg, rgba(5,7,10,.92), transparent 58%), url(" + artwork.previewUrl + ")" } : undefined}>
      <div className="result-badge"><span />生成完成 · 已保存</div>
      <div className="result-meta">
        <div><p className="eyebrow">Generation complete</p><h2>{artwork?.title ?? "作品正在加载"}</h2><p>{artwork ? artwork.width + " × " + artwork.height + " · " + artwork.aspectRatio : "正在读取作品信息"}</p></div>
        {artwork && <div className="result-actions"><button className={artwork.isFavorite ? "icon-button liked" : "icon-button"} onClick={onFavorite} aria-label={artwork.isFavorite ? "取消收藏" : "收藏"}><Heart size={19} fill={artwork.isFavorite ? "currentColor" : "none"} /></button><button className="button button-primary" onClick={onDownload} disabled={downloadBusy}><Download size={17} />{downloadBusy ? "准备中" : "下载壁纸"}</button></div>}
      </div>
    </div>
  );
}
